import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bell, Mail, MessageCircle, Send, CheckCircle2, XCircle } from "lucide-react";
import { fmtIDR } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";

const NotificationSettings = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [cfg, setCfg] = useState({
    enabled_susut: true, enabled_ratio: true,
    owner_email: "", owner_whatsapp: "",
    susut_threshold_pct: 0.5, ratio_threshold: 1.0,
    email_channel_enabled: false, whatsapp_channel_enabled: false,
  });
  const [alerts, setAlerts] = useState([]);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    const c = await api.get("/notifications/config"); setCfg((prev) => ({ ...prev, ...c.data }));
    const a = await api.get("/notifications/alerts"); setAlerts(a.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const { email_channel_enabled, whatsapp_channel_enabled, ...body } = cfg;
      await api.put("/notifications/config", body);
      toast.success("Konfigurasi tersimpan");
      load();
    } catch (e) { toast.error(e.response?.data?.detail?.[0]?.msg || "Gagal menyimpan"); }
  };

  const testNotif = async () => {
    setTesting(true);
    try {
      const r = await api.post("/notifications/test");
      const d = r.data;
      toast.success(`Test: Email=${d.email_sent ? "OK" : "SKIP"} · WA=${d.whatsapp_sent ? "OK" : "SKIP"}`);
      load();
    } catch (e) { toast.error("Test gagal"); }
    finally { setTesting(false); }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-extrabold tracking-tight text-slate-900">Notifikasi Susut & Likuiditas</h1>
        <p className="text-sm text-slate-500 mt-1">Alert real-time via email & WhatsApp saat susut BBM &gt; 0,5% atau current ratio &lt; 1</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className={cfg.email_channel_enabled ? "border-emerald-200" : "border-slate-200"}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg grid place-items-center ${cfg.email_channel_enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
              <Mail className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold">Email (Resend)</div>
              <div className="text-xs text-slate-500">
                {cfg.email_channel_enabled ? <><CheckCircle2 className="w-3 h-3 inline text-emerald-500" /> Aktif</> : <><XCircle className="w-3 h-3 inline text-slate-400" /> Belum dikonfigurasi</>}
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={cfg.whatsapp_channel_enabled ? "border-emerald-200" : "border-slate-200"}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg grid place-items-center ${cfg.whatsapp_channel_enabled ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>
              <MessageCircle className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold">WhatsApp (Twilio)</div>
              <div className="text-xs text-slate-500">
                {cfg.whatsapp_channel_enabled ? <><CheckCircle2 className="w-3 h-3 inline text-emerald-500" /> Aktif — sandbox</> : <><XCircle className="w-3 h-3 inline text-slate-400" /> Belum dikonfigurasi</>}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold flex items-center gap-2"><Bell className="w-5 h-5" />Pengaturan Alert</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Email Owner (Penerima)</Label>
              <Input type="email" value={cfg.owner_email} onChange={(e) => setCfg((c) => ({ ...c, owner_email: e.target.value }))} disabled={!isAdmin} placeholder="owner@example.com" data-testid="notif-email-input" />
            </div>
            <div>
              <Label>WhatsApp Owner (E.164, contoh +628123456789)</Label>
              <Input value={cfg.owner_whatsapp} onChange={(e) => setCfg((c) => ({ ...c, owner_whatsapp: e.target.value }))} disabled={!isAdmin} placeholder="+628123456789" data-testid="notif-wa-input" />
            </div>
          </div>

          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label>Alert Susut BBM</Label>
                <div className="text-xs text-slate-500">Trigger real-time saat rekonsiliasi tangki disimpan</div>
              </div>
              <Switch checked={cfg.enabled_susut} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled_susut: v }))} disabled={!isAdmin} data-testid="notif-susut-toggle" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs w-32">Threshold (%)</Label>
              <Input type="number" step="0.1" value={cfg.susut_threshold_pct} onChange={(e) => setCfg((c) => ({ ...c, susut_threshold_pct: parseFloat(e.target.value) }))} disabled={!isAdmin} className="w-32" />
              <span className="text-xs text-slate-500">dari volume penjualan harian per jenis BBM</span>
            </div>
          </div>

          <div className="border rounded-lg p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <Label>Alert Current Ratio Rendah</Label>
                <div className="text-xs text-slate-500">Trigger saat rasio keuangan dihitung ulang</div>
              </div>
              <Switch checked={cfg.enabled_ratio} onCheckedChange={(v) => setCfg((c) => ({ ...c, enabled_ratio: v }))} disabled={!isAdmin} data-testid="notif-ratio-toggle" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs w-32">Threshold (x)</Label>
              <Input type="number" step="0.1" value={cfg.ratio_threshold} onChange={(e) => setCfg((c) => ({ ...c, ratio_threshold: parseFloat(e.target.value) }))} disabled={!isAdmin} className="w-32" />
              <span className="text-xs text-slate-500">alert saat current ratio di bawah nilai ini</span>
            </div>
          </div>

          {isAdmin && (
            <div className="flex gap-2 pt-2">
              <Button onClick={save} className="bg-[#E30613] hover:bg-[#C8040F]" data-testid="notif-save-button">Simpan Konfigurasi</Button>
              <Button onClick={testNotif} variant="outline" disabled={testing} data-testid="notif-test-button">
                <Send className="w-4 h-4 mr-2" />{testing ? "Mengirim..." : "Kirim Test"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-lg font-bold">Riwayat Alert Terkirim</CardTitle></CardHeader>
        <CardContent>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Waktu</TableHead>
              <TableHead>Tipe</TableHead>
              <TableHead>Outlet</TableHead>
              <TableHead>Detail</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>WhatsApp</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {alerts.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-slate-500 py-8">Belum ada alert terkirim</TableCell></TableRow>}
              {alerts.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="font-mono-fig text-xs">{a.sent_at?.slice(0, 19).replace("T", " ")}</TableCell>
                  <TableCell>
                    {a.type === "susut" ? <Badge className="bg-rose-50 text-rose-700 border border-rose-200">Susut BBM</Badge>
                      : <Badge className="bg-amber-50 text-amber-700 border border-amber-200">Current Ratio</Badge>}
                  </TableCell>
                  <TableCell className="font-mono-fig text-sm">{a.outlet_id}</TableCell>
                  <TableCell className="text-xs text-slate-600 max-w-md">
                    {a.type === "susut"
                      ? `${a.data.fuel_code} · ${a.data.loss_liters?.toFixed(2)} L · ${a.data.ratio_pct?.toFixed(2)}% · ${fmtIDR(a.data.loss_value)}`
                      : `Ratio ${a.data.current_ratio?.toFixed(2)}x · Aset ${fmtIDR(a.data.current_assets)} / Utang ${fmtIDR(a.data.current_liab)}`}
                  </TableCell>
                  <TableCell>{a.email_id ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-slate-300" />}</TableCell>
                  <TableCell>{a.whatsapp_sid ? <CheckCircle2 className="w-4 h-4 text-emerald-600" /> : <XCircle className="w-4 h-4 text-slate-300" />}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationSettings;
